import { useState } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Sparkles, BarChart3, GraduationCap, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AIScoutingSummary({ athleteId, existingSummary }) {
  const [summary, setSummary] = useState(existingSummary || null);
  const [loading, setLoading] = useState(false);

  const generateSummary = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/ai/scouting-summary/${athleteId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data.summary);
        toast.success("AI analysis generated!");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Generation failed");
      }
    } catch (err) {
      toast.error("AI analysis failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="ai-scouting-summary">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-500" />
          AI Scouting Analysis
        </h3>
        <Button
          size="sm"
          onClick={generateSummary}
          disabled={loading}
          className="bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="generate-ai-summary-btn"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analyzing...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-1" /> {summary ? "Regenerate" : "Generate"}
            </>
          )}
        </Button>
      </div>
      {summary ? (
        <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed">
          {summary}
        </div>
      ) : (
        <p className="text-gray-400 text-sm italic">
          Click "Generate" to create an AI-powered scouting analysis from this player's metrics and data.
        </p>
      )}
    </div>
  );
}

export function AICollegeProjection({ athleteId, existingProjection }) {
  const [projection, setProjection] = useState(existingProjection || null);
  const [loading, setLoading] = useState(false);

  const generateProjection = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/ai/college-projection/${athleteId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setProjection(data.projection);
        toast.success("College projection generated!");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Projection failed");
      }
    } catch (err) {
      toast.error("Projection failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="ai-college-projection">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-blue-600" />
          College Fit Projection
        </h3>
        <Button
          size="sm"
          onClick={generateProjection}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="generate-projection-btn"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Projecting...
            </>
          ) : (
            <>
              <GraduationCap className="h-4 w-4 mr-1" /> {projection ? "Regenerate" : "Generate"}
            </>
          )}
        </Button>
      </div>
      {projection ? (
        <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed">
          {projection}
        </div>
      ) : (
        <p className="text-gray-400 text-sm italic">
          Generate an AI projection for college fit level, program recommendations, and development trajectory.
        </p>
      )}
    </div>
  );
}

export function AIPlayerComparison({ playerA, players }) {
  const [selectedB, setSelectedB] = useState("");
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  // Ensure players is always an array
  const playersList = Array.isArray(players) ? players : [];

  const compare = async () => {
    if (!selectedB) {
      toast.error("Select a player to compare");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/ai/player-comparison`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ player_a_id: playerA.athlete_id, player_b_id: selectedB }),
      });
      if (res.ok) {
        const data = await res.json();
        setComparison(data);
        toast.success("Comparison complete!");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Comparison failed");
      }
    } catch (err) {
      toast.error("Comparison failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="ai-player-comparison">
      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
        <BarChart3 className="h-5 w-5 text-green-600" />
        Player Comparison
      </h3>
      <div className="flex items-end gap-3 mb-4">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Compare with</label>
          <select
            value={selectedB}
            onChange={(e) => setSelectedB(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            data-testid="comparison-select"
          >
            <option value="">Select a player...</option>
            {playersList
              .filter((p) => p.athlete_id !== playerA.athlete_id)
              .map((p) => (
                <option key={p.athlete_id} value={p.athlete_id}>
                  {p.name} — {p.school || "Unknown"} ({p.position_primary || p.position || "--"})
                </option>
              ))}
          </select>
        </div>
        <Button
          onClick={compare}
          disabled={loading || !selectedB}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="compare-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4 mr-1" />}
          Compare
        </Button>
      </div>
      {comparison && (
        <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed border-t pt-4">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-500">
            {comparison.player_a?.name} vs {comparison.player_b?.name}
          </div>
          {comparison.comparison}
        </div>
      )}
    </div>
  );
}
